//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { FieldErrors } from 'react-hook-form';

import {
  CampaignRequestTasks,
  CreatorTargetAge,
  CreatorTargetAgeRange,
} from '@apis/campaign-requests/creators/CreatorsCampaignRequestApiModel';
import {
  allowedAlphabeticLettersInName,
  allowedCharactersInName,
} from '@campaigns/components/campaign-requests/creator-campaigns/shared/utils/creatorsCampaignsUtils';
import {
  AgeRadioItemValueType,
  UpdateCampaignRequestTasksFormValues,
} from '@campaigns/components/campaigns-requests/creator-campaigns/CreatorsCampaignRequestTypes';
import {
  EditableBVSamplingProduct,
  EditableBVSamplingProductVariation,
} from '@campaigns/types/sampling/SamplingProductTypes';
import { isDefined, isEmptyValue } from '@utils/filterPredicates';
import { capitalize, toGrammaticallyCorrectList } from '@utils/stringUtils';

export function validateNumber<T>(value: T): boolean {
  return isDefined(value) ? !Number.isNaN(Number(value)) : true;
}

/**
 * Validates if the field value is a positive number
 * @param value the form field value
 */
export function validatePositiveNumber(value: string | undefined): boolean {
  const valueNumber = Number(value);
  return isDefined(value)
    ? !Number.isNaN(valueNumber) && valueNumber >= 0
    : true;
}

/**
 * Validates if the field value is a positive integer not a zero
 * @param value the form field value
 */
export function validatePositiveNotZeroInteger(
  value: string | undefined,
): boolean {
  if (!value) {
    return true;
  }

  const valueNumber = Number(value);
  return (
    !Number.isNaN(Number(value)) &&
    Number.isInteger(valueNumber) &&
    valueNumber >= 1
  );
}

/**
 * Validates two-field range values
 * @param value the form field value
 * @param operator e.g. '<' less than, '>' larger than
 * @param watchedValue the watched form value that will be checked against
 */
export function validateRange<T extends number | string | undefined>(
  value: T,
  operator: '<' | '>',
  watchedValue: T,
): boolean {
  if (!value) {
    return true;
  }

  if (!validateNumber(value)) {
    return false;
  }

  const valueNumber = Number(value);
  const watchedValueNumber = watchedValue === '' ? NaN : Number(watchedValue);
  if (Number.isNaN(watchedValueNumber)) {
    return true;
  }

  return operator === '>'
    ? valueNumber > watchedValueNumber
    : valueNumber < watchedValueNumber;
}

/*
 * Returns CampaignRequestTasks object from getTasksObjectFromFormValues
 * @param formValues : getTasksObjectFromFormValues type object
 */
export const getTasksObjectFromFormValues = (
  formValues: UpdateCampaignRequestTasksFormValues,
): CampaignRequestTasks => {
  return {
    ...formValues,
    tiktokTasks: formValues.tiktokTasks
      ? {
          ...formValues.tiktokTasks,
          videos: formValues.tiktokTasks.videos?.filter((video) => !!video),
        }
      : undefined,
  };
};

/**
 * Transforms age radiobox field to CreatorTargetAge API interface
 * @param val age range radiobox field value
 * @returns CreatorTargetAge converted from form field value to match the API structure
 */
export const normalizeAgeFormValue = (
  val: AgeRadioItemValueType,
): CreatorTargetAge => {
  return val === 'isAny'
    ? {
        isAny: true,
      }
    : {
        isAny: false,
        range: {},
      };
};

/**
 * Transforms age range : CreatorTargetAgeRange to age form value
 * @param ageRange fetched CreatorTargetAge value, can also be undefined if not set
 * @returns AgeRadioItemValueType a form field value for age field
 */
export const getNormalizedAgeRadioValue = (
  ageRange: CreatorTargetAgeRange | undefined,
): AgeRadioItemValueType => {
  return !isDefined(ageRange) ? 'isAny' : 'range';
};

/**
 * Parses a full name string and returns an object with `firstname` and `lastname` properties.
 *
 * The function assumes:
 * - The first name is the first word in the string.
 * - The last name is the last word in the string.
 * - If the full name includes hyphens or special characters, it only considers alphanumeric characters for the first and last names.
 *
 * Examples:
 * - parseFullName('Lǐ Nà') ==> { firstname: 'Lǐ', lastname: 'Nà' }
 * - parseFullName('Mary-Jane Watson-Parker') ==> { firstname: 'Mary-Jane', lastname: 'Watson-Parker' }
 * - parseFullName('!@#$%^&*()') ==> { firstname: '', lastname: '' }
 * - parseFullName('John!@# Doe123') ==> { firstname: 'John', lastname: 'Doe' }
 * - parseFullName('D\'Angelo Russell') ==> { firstname: 'D'Angelo', lastname: 'Russell' }
 * - parseFullName('Venkata Satyanarayana Prabhas Raju Uppalapati') ==> { firstname: 'Venkata', lastname: 'Uppalapati' }
 * - parseFullName('Dr. John Smith') ==> { firstname: 'Dr', lastname: 'Smith' }
 * - parseFullName('drake_mal') ==> { firstname: 'Drake', lastname: 'Mal' }
 *
 * @param name - The full name string to parse.
 * @returns An object with `firstname` and `lastname` properties representing the first and last names respectively.
 */

export const parseFullName = (
  name?: string,
  username?: string,
): { firstName: string; lastName: string } => {
  // Cleans string by removing anything which is not inside the allowed characters list
  const cleanName = (str: string): string =>
    str.replace(new RegExp(`[^${allowedCharactersInName}]`, 'g'), '');

  // Helper function to check if a string contains at least one letter
  const hasAtLeastOneAlphabeticLetter = (str: string): boolean => {
    return new RegExp(`[${allowedAlphabeticLettersInName}]`).test(str);
  };

  // Clean the name before splitting
  let cleanedFullName = cleanName(name ?? '');

  // If cleaned name resulted as empty with the influencer's name, try to extract from username as a fallback
  if (!cleanedFullName && username) {
    cleanedFullName = cleanName(username);
  }
  const nameParts = cleanedFullName
    .split(/[_ ]+/)
    .filter(Boolean)
    .map((part) => part.replace(/\./g, ''))
    // Only keep parts that have at least one letter
    .filter(hasAtLeastOneAlphabeticLetter);

  if (nameParts.length === 0) {
    return { firstName: '', lastName: '' };
  }

  if (nameParts.length === 1 && nameParts[0]) {
    // For single word names, use the same value for both firstName and lastName
    return { firstName: capitalize(nameParts[0]), lastName: '' };
  }

  // Get first name (first word) and last name (last word)
  const firstName = capitalize(nameParts[0] ?? '');
  const lastName = capitalize(nameParts[nameParts.length - 1] ?? '');

  return { firstName, lastName };
};

/**
 * Removes properties from an object where the value is empty (an empty string, null, or undefined).
 *
 * @template T - The type of the object.
 * @param obj - The object to omit empty values from.
 * @returns A new object with only non-empty values.
 */
export const omitEmptyValues = <T extends object>(obj: T): T => {
  const defaultObject = {} as T;
  return Object.entries(obj).reduce<T>((acc, [key, value]) => {
    if (!isEmptyValue(value)) {
      acc[key as keyof T] = value;
    }
    return acc;
  }, defaultObject);
};

interface FormErrors {
  requiredFields: string[];
  otherErrors: string[];
}

// Special words that should remain uppercase in error messages
const SPECIAL_WORDS = ['ID', 'GTIN'] as const;

/**
 * Formats a label by keeping special words uppercase and lowercasing everything else
 * @example
 * formatLabel('Product ID') => 'product ID'
 * formatLabel('GTIN Number') => 'GTIN number'
 * formatLabel('Product Name') => 'product name'
 */
const formatLabel = (label: string): string => {
  const specialWordsPattern = new RegExp(
    `\\b(${SPECIAL_WORDS.join('|')})\\b`,
    'gi',
  );
  return label
    .toLowerCase()
    .replace(specialWordsPattern, (match) => match.toUpperCase());
};

/**
 * Processes form errors and returns formatted error messages
 * @param errors - Form errors to process (from react-hook-form's FieldErrors)
 * @param fieldLabels - Mapping of field keys to their display labels
 * @param prefix - Optional prefix for error messages (e.g. 'Variation 1: ')
 * @returns Array of formatted error messages
 *
 * @example
 * // For product-level errors:
 * processFormErrors<EditableBVSamplingProduct>(productErrors, fieldLabels)
 *
 * // For variation-level errors:
 * processFormErrors<EditableBVSamplingProductVariation>(variationErrors, fieldLabels, 'Variation 1: ')
 */
const processFormErrors = <T extends object>(
  errors: FieldErrors<T>,
  fieldLabels: Record<string, string>,
  prefix = '',
): string[] => {
  const formErrors: FormErrors = { requiredFields: [], otherErrors: [] };
  const errorMessages: string[] = [];

  Object.entries(errors)
    .filter(([_, value]) => isDefined(value.message) && isDefined(value.type))
    .forEach(([key, value]) => {
      const label = fieldLabels[key] ?? key;
      const formattedLabel =
        value.type === 'required' ? formatLabel(label) : label;

      if (value.type === 'required') {
        formErrors.requiredFields.push(formattedLabel);
      } else if (value.message) {
        formErrors.otherErrors.push(value.message);
      }
    });

  // Add errors and group required fields together
  errorMessages.push(...formErrors.otherErrors.map((msg) => prefix + msg));
  if (formErrors.requiredFields.length > 0) {
    const fields = toGrammaticallyCorrectList(formErrors.requiredFields);
    errorMessages.push(
      `${prefix}${capitalize(fields)} ${formErrors.requiredFields.length > 1 ? 'are' : 'is'} required`,
    );
  }

  return errorMessages;
};

/**
 * Processes form errors for a BV Product form and returns formatted error messages.
 * Groups required field errors together and formats messages consistently.
 *
 * @param errors - Form errors from react-hook-form
 * @param fieldLabels - Mapping of field keys to their display labels
 *
 * @returns Array of formatted error messages
 *
 * @example
 * // Input errors:
 * {
 *   name: {
 *     type: 'required',
 *     message: 'Please enter a product name'
 *   },
 *   brand: {
 *     type: 'required',
 *     message: 'Please enter a product brand'
 *   },
 *   variations: [
 *     {
 *       quantity: {
 *         type: 'validate',
 *         message: 'Quantity must be greater than 0'
 *       }
 *     }
 *   ]
 * }
 *
 * // Returns:
 * [
 *   'Product name and product brand are required',
 *   'Variation 1: Quantity must be greater than 0'
 * ]
 */
export const getBVProductFormErrorMessages = (
  errors: FieldErrors<EditableBVSamplingProduct>,
  fieldLabels: Record<string, string>,
): string[] => {
  const errorMessages: string[] = [];

  // Process product-level errors
  errorMessages.push(...processFormErrors(errors, fieldLabels));

  // Process variations
  if (errors.variations) {
    const variations =
      errors.variations as unknown as FieldErrors<EditableBVSamplingProductVariation>[];
    variations.forEach((variation, index) => {
      errorMessages.push(
        ...processFormErrors(
          variation,
          fieldLabels,
          `Variation ${index + 1}: `,
        ),
      );
    });
  }

  return errorMessages;
};
